import { Component, ErrorInfo, ReactNode } from "react";
import { trackException } from "@onetasklist/shared";
import i18n from "../../i18n";
import "./ErrorBoundary.scss";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error("[ErrorBoundary] Uncaught error:", error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });

    trackException(error, {
      componentStack: errorInfo.componentStack ?? "",
    });
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="error-boundary">
          <div className="error-boundary__panel">
            <h2 className="error-boundary__title">
              <i className="fas fa-exclamation-triangle error-boundary__title-icon"></i>
              {i18n.t("errors.boundary.title")}
            </h2>
            <p className="error-boundary__message">
              {i18n.t("errors.boundary.message")}
            </p>
          </div>

          {import.meta.env.DEV && this.state.error && (
            <details className="error-boundary__details">
              <summary className="error-boundary__summary">
                {i18n.t("errors.boundary.detailsTitle")}
              </summary>
              <div className="error-boundary__stack">
                <pre className="error-boundary__stack-pre">
                  <strong>{i18n.t("errors.boundary.errorLabel")}</strong>{" "}
                  {this.state.error.toString()}
                  {"\n\n"}
                  {this.state.errorInfo && (
                    <>
                      <strong>
                        {i18n.t("errors.boundary.componentStackLabel")}
                      </strong>
                      {this.state.errorInfo.componentStack}
                    </>
                  )}
                </pre>
              </div>
            </details>
          )}

          <div className="error-boundary__actions">
            <button
              onClick={this.handleReset}
              className="error-boundary__btn error-boundary__btn--primary"
            >
              {i18n.t("errors.boundary.tryAgain")}
            </button>
            <button
              onClick={() => window.location.reload()}
              className="error-boundary__btn error-boundary__btn--secondary"
            >
              {i18n.t("common.actions.reload")}
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
